import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { differenceInSeconds } from 'date-fns';

import {
  IllustrationPrefilledDetails,
  Loader,
  SpacerHorizontal,
  SpacerVertical,
  Typography,
} from '@klarna/bubble-ui';
import * as Sentry from '@sentry/react';

import { Message, translator } from 'mage-i18n';
import { isRunningContextCypress } from 'moet-test-utils/cypress/utils';
import { MOET_CONSTANTS } from 'moet-core';
import { MessagingStep } from '@/components';
import { CAPS } from '@/constants';
import {
  getPayoutsRequestedAt,
  getTransactionsRequestedAt,
  getUserMerchantId,
  getTimestampOfLastAcceptedToc,
} from '@/selectors/merchant';
import { withChainedCapabilitiesBeforeNext } from '@/flow/common';
import { getAfterFlowStepForMerchantApplication } from '@/flow/configs/selectors';

const tPrefix = 'products-fe.containers.application_checking';

export class ApplicationCheckingV2 extends React.Component {
  constructor(props) {
    super(props);
    const timeout =
      (isRunningContextCypress(window)
        ? MOET_CONSTANTS.CELEBRATION_TIME_WINDOW_CYPRESS_SECONDS
        : MOET_CONSTANTS.CELEBRATION_TIME_WINDOW_SECONDS) * 1000;

    setTimeout(this.navigate.bind(this), timeout);
  }

  async navigate() {
    const {
      afterFlowStep,
      mid,
      onNext,
      basePath,
      history,
      payoutsRequestedAt,
      transactionsRequestedAt,
      maxTocAcceptedTimestamp,
    } = this.props;

    onNext({ payload: { params: { mid } }, scrollBeforeNext: true });

    // If the request take too long to go through, it can be that the user gets
    // stuck in between flows
    const timeSinceLastStatusChange = differenceInSeconds(
      new Date(),
      new Date(transactionsRequestedAt || payoutsRequestedAt) || maxTocAcceptedTimestamp,
    );
    if (
      timeSinceLastStatusChange >=
      (isRunningContextCypress(window)
        ? MOET_CONSTANTS.CELEBRATION_TIME_WINDOW_CYPRESS_SECONDS
        : MOET_CONSTANTS.CELEBRATION_TIME_WINDOW_SECONDS)
    ) {
      // If the merchant is missing transactions requested and payouts requested something went
      // wrong, we throw a sentry exception to alert us
      if (!transactionsRequestedAt && !payoutsRequestedAt) {
        Sentry.captureException({
          message:
            'Merchant submited application but after some time transactions requested was empty',
          level: 'error',
        });
      }
      history.push(`${basePath}/${afterFlowStep}` + history.location.search);
    }
  }

  render() {
    const { t } = this.props;
    return (
      <MessagingStep
        illustration={<IllustrationPrefilledDetails />}
        title={[t(`${tPrefix}.title`)]}
      >
        <SpacerVertical spaceToken="space/150" />
        <Typography textToken="text-style/text/super-paragraphs/tall/regular">
          <div style={{ display: 'flex' }}>
            <div style={{ paddingTop: '3px' }}>
              <Loader />
            </div>
            <SpacerHorizontal spaceToken="space/150" />
            <Message path={`${tPrefix}.message`} />
          </div>
        </Typography>
      </MessagingStep>
    );
  }
}

ApplicationCheckingV2.propTypes = {
  afterFlowStep: PropTypes.string.isRequired,
  basePath: PropTypes.string.isRequired,
  history: PropTypes.object.isRequired,
  maxTocAcceptedTimestamp: PropTypes.instanceOf(Date).isRequired,
  mid: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  payoutsRequestedAt: PropTypes.string,
  transactionsRequestedAt: PropTypes.string,
};

export default compose(
  connect(state => ({
    t: translator(state),
    afterFlowStep: getAfterFlowStepForMerchantApplication(state),
    maxTocAcceptedTimestamp: getTimestampOfLastAcceptedToc(state),
    mid: getUserMerchantId(state),
    payoutsRequestedAt: getPayoutsRequestedAt(state),
    transactionsRequestedAt: getTransactionsRequestedAt(state),
  })),
  withChainedCapabilitiesBeforeNext({
    getMerchant: CAPS.V2_MERCHANTS_GET,
    getProducts: CAPS.V2_PRODUCTS_GET,
  }),
)(ApplicationCheckingV2);
